import client from 'client'
import type {
  GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationRequest,
  GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationResponse,
  GetSummarisedTimeSeriesReportReportTimeSeriesPostQueryParams,
  GetSummarisedTimeSeriesReportReportTimeSeriesPost422,
} from '../../types/GetSummarisedTimeSeriesReportReportTimeSeriesPost.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getSummarisedTimeSeriesReportReportTimeSeriesPostQueryKey = (
  data?: GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationRequest,
  params?: GetSummarisedTimeSeriesReportReportTimeSeriesPostQueryParams,
) => [{ url: '/report/time_series' }, ...(params ? [params] : []), ...(data ? [data] : [])] as const

export type GetSummarisedTimeSeriesReportReportTimeSeriesPostQueryKey = ReturnType<typeof getSummarisedTimeSeriesReportReportTimeSeriesPostQueryKey>

/**
 * @summary Get Summarised Time Series Report
 * {@link /report/time_series}
 */
export async function getSummarisedTimeSeriesReportReportTimeSeriesPost(
  data?: GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationRequest,
  params?: GetSummarisedTimeSeriesReportReportTimeSeriesPostQueryParams,
  config: Partial<RequestConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationResponse,
    ResponseErrorConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPost422>,
    GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationRequest
  >({ method: 'POST', url: `/report/time_series`, params, data, ...requestConfig })
  return res
}

export function getSummarisedTimeSeriesReportReportTimeSeriesPostQueryOptions(
  data?: GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationRequest,
  params?: GetSummarisedTimeSeriesReportReportTimeSeriesPostQueryParams,
  config: Partial<RequestConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationRequest>> & { client?: typeof client } = {},
) {
  const queryKey = getSummarisedTimeSeriesReportReportTimeSeriesPostQueryKey(data, params)
  return queryOptions<
    ResponseConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationResponse>,
    ResponseErrorConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPost422>,
    ResponseConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getSummarisedTimeSeriesReportReportTimeSeriesPost(data, params, config)
    },
  })
}

/**
 * @summary Get Summarised Time Series Report
 * {@link /report/time_series}
 */
export function useGetSummarisedTimeSeriesReportReportTimeSeriesPost<
  TData = ResponseConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationResponse>,
  TQueryData = ResponseConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationResponse>,
  TQueryKey extends QueryKey = GetSummarisedTimeSeriesReportReportTimeSeriesPostQueryKey,
>(
  data?: GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationRequest,
  params?: GetSummarisedTimeSeriesReportReportTimeSeriesPostQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationResponse>,
        ResponseErrorConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPost422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getSummarisedTimeSeriesReportReportTimeSeriesPostQueryKey(data, params)

  const query = useQuery({
    ...(getSummarisedTimeSeriesReportReportTimeSeriesPostQueryOptions(data, params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetSummarisedTimeSeriesReportReportTimeSeriesPost422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}